import { useQuery } from "@tanstack/react-query";
import {
  parseAsArrayOf,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import { useCallback, useMemo } from "react";
import {
  mapProductTypeToCategory as mapProductTypeToCategory_Util,
  mapVendorToBrand as mapVendorToBrand_Util,
} from "@/lib/category-mapping";
import { orpc } from "@/utils/orpc";
import {
  type Brand,
  type ProductCategory,
  SORT_OPTIONS,
  type SortOption,
} from "@/utils/routes";

// Shared query configuration for no-cache approach
const NO_CACHE_CONFIG = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: true,
  retry: 2,
} as const;

/**
 * Transform Shopify product type to frontend category format
 * Updated to handle production categories dynamically
 */
function mapProductTypeToCategory(productType: string): string {
  return mapProductTypeToCategory_Util(productType);
}

/**
 * Transform Shopify vendor to frontend brand format
 */
export function mapVendorToBrand(vendor: string): string {
  return mapVendorToBrand_Util(vendor);
}

/**
 * Custom nuqs-based hook for managing shop filter state with URL synchronization.
 * Replaces manual URLSearchParams handling with type-safe nuqs hooks.
 */
export function useShopFilters() {
  // Fetch available categories directly from Shopify
  const { data: rawCategories = [] } = useQuery({
    queryKey: ["shopify", "productTypes"],
    queryFn: async () => {
      const result = await orpc.shopify.getProductTypes.call({
        first: 100,
      });
      return result;
    },
    ...NO_CACHE_CONFIG,
  });

  // Fetch available brands directly from Shopify
  const { data: rawBrands = [] } = useQuery({
    queryKey: ["shopify", "productVendors"],
    queryFn: async () => {
      const result = await orpc.shopify.getProductVendors.call({
        first: 250,
      });
      return result;
    },
    ...NO_CACHE_CONFIG,
  });

  // Transform raw data to frontend format
  const availableCategories = rawCategories.map(mapProductTypeToCategory);
  const availableBrands = rawBrands.map(mapVendorToBrand);

  const [filters, setFilters] = useQueryStates(
    {
      categories: parseAsArrayOf(
        parseAsString, // Allow any string category from Shopify
        ","
      ).withDefault([]),
      brands: parseAsArrayOf(
        parseAsString, // Allow any string brand from Shopify
        ","
      ).withDefault([]),
      sort: parseAsStringEnum([...SORT_OPTIONS]).withDefault(SORT_OPTIONS[0]),
      search: parseAsString.withDefault(""),
      navSearch: parseAsString.withDefault(""), // Navbar search parameter
      minPrice: parseAsFloat,
      maxPrice: parseAsFloat,
      page: parseAsInteger.withDefault(1),
    },
    {
      shallow: false, // Ensure deep comparison for array updates
    }
  );

  // Transform filters to match existing ShopFilters interface
  const shopFilters = useMemo(() => {
    // Get effective search (prioritize navSearch over search)
    const effectiveSearch = filters.navSearch || filters.search;

    const result = {
      categories:
        filters.categories.length > 0
          ? (filters.categories as ProductCategory[])
          : undefined,
      brands:
        filters.brands && filters.brands.length > 0
          ? (filters.brands as Brand[])
          : undefined,
      sort: filters.sort as SortOption,
      search: effectiveSearch || undefined,
      minPrice: filters.minPrice ?? undefined,
      maxPrice: filters.maxPrice ?? undefined,
    };

    return result;
  }, [filters]);

  // Helper functions for updating specific filters
  const updatePage = useCallback(
    (page: number) => {
      setFilters({ page });
    },
    [setFilters]
  );

  const updateCategories = useCallback(
    (categories: ProductCategory[] | undefined) => {
      // Accept all categories from Shopify (no longer filtering to hardcoded list)
      const validCategories = (categories ?? []).filter(Boolean); // Just filter out empty/null values
      setFilters({ categories: validCategories, page: 1 });
    },
    [setFilters]
  );

  const toggleCategory = useCallback(
    (category: ProductCategory) => {
      // Accept any category from Shopify (no longer restricting to hardcoded list)
      setFilters((prev) => {
        const currentCategories = prev.categories as string[];
        const isSelected = currentCategories.includes(category);
        const newCategories = isSelected
          ? currentCategories.filter((c) => c !== category)
          : [...currentCategories, category];

        return { ...prev, categories: newCategories, page: 1 };
      });
    },
    [setFilters]
  );

  const updateSort = useCallback(
    (sort: SortOption) => {
      setFilters({ sort, page: 1 });
    },
    [setFilters]
  );

  const updateSearch = useCallback(
    (search: string | undefined) => {
      setFilters({ search: search || "", page: 1 });
    },
    [setFilters]
  );

  const updateNavSearch = useCallback(
    (navSearch: string | undefined) => {
      setFilters({ navSearch: navSearch || "", page: 1 });
    },
    [setFilters]
  );

  const clearNavSearch = useCallback(() => {
    setFilters({ navSearch: "" });
  }, [setFilters]);

  const updatePriceRange = useCallback(
    (minPrice?: number, maxPrice?: number) => {
      setFilters({ minPrice, maxPrice, page: 1 });
    },
    [setFilters]
  );

  const clearAllFilters = useCallback(() => {
    setFilters({
      categories: [],
      brands: [],
      sort: SORT_OPTIONS[0],
      search: "",
      minPrice: null,
      maxPrice: null,
      page: 1,
    });
  }, [setFilters]);

  const clearCategories = useCallback(() => {
    updateCategories(undefined);
  }, [updateCategories]);

  const updateBrands = useCallback(
    (brands: Brand[] | undefined) => {
      const validBrands = (brands ?? []).filter(Boolean);
      setFilters({ brands: validBrands, page: 1 });
    },
    [setFilters]
  );

  const clearBrands = useCallback(() => {
    updateBrands(undefined);
  }, [updateBrands]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: shopFilters already includes all necessary dependencies
  return useMemo(
    () => ({
      // Current filter state (URL-synced)
      filters: shopFilters,
      page: filters.page ?? 1,

      // Individual filter values for easy access
      categories: shopFilters.categories,
      sort: shopFilters.sort,
      search: shopFilters.search,
      navSearch: filters.navSearch,
      minPrice: shopFilters.minPrice,
      maxPrice: shopFilters.maxPrice,

      // Available options from Shopify
      availableCategories,
      availableBrands,

      // Update functions
      updatePage,
      updateCategories,
      toggleCategory,
      updateSort,
      updateSearch,
      updateNavSearch,
      updatePriceRange,
      clearAllFilters,
      clearCategories,
      clearNavSearch,
      updateBrands,
      clearBrands,

      // Generic update function for compatibility
      updateFilters: setFilters,
    }),
    [
      shopFilters,
      filters.page,
      filters.navSearch,
      availableCategories,
      availableBrands,
      updateNavSearch,
      clearNavSearch,
    ]
  );
}
